import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import 'moment-timezone';

import moment from 'moment';

import type { Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class ClientsPromoBannerComponent extends Component {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;

	origin = 'client_page_banner';

	get isBannerShown() {
		return (
			this.billingService.isAgencyAdmin &&
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins &&
			this.isEligibleToShow
		);
	}

	get timezone() {
		return this.currentUserService.model?.timezone ?? 'UTC';
	}

	get isEligibleToShow() {
		const closedTimestamp = this.userSettingsService.clientPromoBannerClosedTimestamp;

		let isEligibleToShow = false;

		if (!closedTimestamp) {
			// Never closed before, so eligible to show
			isEligibleToShow = true;
		} else {
			// Check if we're in a new day compared to when the banner was closed
			const timezone = this.timezone;
			const closedDate = moment.unix(closedTimestamp).tz(timezone).startOf('day');
			const currentDate = moment().tz(timezone).startOf('day');

			isEligibleToShow = !closedDate.isSame(currentDate, 'day');
		}

		return isEligibleToShow;
	}

	@action
	bookACall(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.analyticsService.event(TrackingEvent.CALL_INITIATED, { origin: this.origin });

		this.accountService.requestSetupHelp();
	}

	@action
	learnMore(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.analyticsService.event(TrackingEvent.RESELLER_PROMO_CLICKED, {
			origin: this.origin,
		});

		window.open('https://databox.com/reseller', '_blank');
	}

	@action
	close() {
		void this.userSettingsService.save(
			UserSettingsKey.CLIENT_PROMO_BANNER_CLOSED_TIMESTAMP,
			moment().tz(this.timezone).unix(),
		);
	}

	<template>
		{{#if this.isBannerShown}}
			<div class="promo-banner promo-banner--clients">
				<a role="button" class="promo-banner__close" {{on "click" this.close}}>
					<CommonUiIconComponent @name="Close" @size="sm" />
				</a>
				<div class="promo-banner__content">
					<h2>New: Become a Databox Solutions partner</h2>
					<p>
						Increase client retention, grow revenue, and build a strategic edge. Learn
						how our solutions partners are embedding themselves in key C-Suite client
						conversations by offering BI as a service.
					</p>
					<div class="promo-banner__buttons">
						<SubmitButtonComponent
							@text="Book a call"
							@icon="Calendar"
							@onTrigger={{this.bookACall}}
						/>
						<SubmitButtonComponent
							@text="Learn more"
							@theme="blue-outlined"
							@onTrigger={{this.learnMore}}
						/>
					</div>
				</div>
				<img
					class="promo-banner__image"
					src="/images/clients/promo.png"
					alt="Become a Databox Solutions partner"
				/>
			</div>
		{{/if}}
	</template>
}
